<template>
  <div>
    <LoaderView v-if="!branding" />
    <!-- Teleported so the toast region is a sibling of the modal roots, not a descendant
         of the app that a modal inerts. position: fixed, so it does not move. -->
    <Teleport to="body">
      <ToasterView />
    </Teleport>
    <div class="mainView" v-if="branding">
      <a href="#main-content" class="skipLink">{{ t('common.skipToMainContent') }}</a>
      <HeaderView />
      <router-view
        class="content"
        :id="routeProvidesMain ? null : 'main-content'"
        :role="routeProvidesMain ? null : 'main'"
        v-slot="{ Component }"
      >
        <transition name="page" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { useUnleashContext } from '@unleash/proxy-client-vue'
import { useI18n } from 'vue-i18n'
import { createUnleashInfo } from '@/utils/unleashInfo'
import { watchFieldErrors } from '@/utils/fieldErrors'
import { pickFocusColor } from '@/utils/contrast'

import HeaderView from '@/components/Main/Layout/HeaderView.vue'
import ToasterView from '@/components/Toaster/ToasterView.vue'
import LoaderView from '@/components/Loaders/LoaderView.vue'
import { analyticsAllowedOnHost } from '@/utils/analyticsHost'

export default {
  components: {
    HeaderView,
    ToasterView,
    LoaderView,
  },
  setup() {
    // Grab the real context-update function provided by the Unleash plugin.
    // The plugin only exposes it via provide/inject — there is no
    // `this.$unleash` / `window.__unleashClient`, so this is the only way
    // to update the context (and trigger a re-fetch) at runtime.
    const { t } = useI18n()
    const updateUnleashContext = useUnleashContext()
    return { t, updateUnleashContext }
  },
  computed: {
    ...mapState(['branding', 'storeConfig']),
    // MenuView and ContentHubView render their own <main> so the page footer can sit
    // beside it as a top-level contentinfo landmark instead of nested inside <main>.
    // On those routes the routed component's root is a plain .content scroll container;
    // elsewhere id/role fall through onto it to carry role="main" + the skip-link target.
    routeProvidesMain() {
      return ['MenuView', 'ContentHubView'].includes(this.$route.name)
    },
  },
  watch: {
    branding(newVal) {
      this.setFonts(newVal)
      this.setStyling(newVal)
      this.setHeadTags(newVal)
      this.addPixelScript()
    },
    storeConfig: {
      immediate: true,
      handler(newVal) {
        // set app name
        if (newVal) {
          let appName = newVal.chain_display_name
          const appNameMeta = document.querySelector('meta[name="application-name"]')
          if (appNameMeta) appNameMeta.content = appName

          // Update Unleash context with chain information
          this.syncUnleashContext(newVal)
        }
      },
    },
  },
  methods: {
    ...mapMutations(['SET_LOGGED_IN', 'SET_ORDER_DATA', 'SET_DAY_AND_TIME_SELECTED']),
    async syncUnleashContext(storeConfig) {
      try {
        // Build context (chain) from the freshly loaded store config. On a
        // first visit / incognito the chain is not yet in localStorage when
        // the Unleash client boots, so the initial context has no `chain` and
        // the `chain IN (...)` constraint never matches. We must push it here.
        const context = createUnleashInfo(storeConfig)

        if (typeof this.updateUnleashContext === 'function') {
          // Triggers a re-fetch; useFlag is reactive so gated UI updates in-session.
          await this.updateUnleashContext(context)
        }
      } catch (error) {
        console.warn('Failed to update Unleash context:', error)
      }
    },
    goToLocationsPage() {
      this.$router.push({ name: 'HomeView' })
    },
    // Registers one branding font slot under a family name we control.
    //
    // We deliberately ignore `font_name` coming from the admin dashboard as the
    // CSS family name: chains can (and do) give two different font files the
    // same name, in which case the face added last silently wins for every
    // slot that shares that name. Slot-scoped family names keep each upload
    // isolated no matter what the dashboard says.
    registerFont(family, cssVar, font, fontPromises) {
      // The watcher can run again on a branding change; drop the previous face
      // so faces don't pile up under the same family.
      document.fonts.forEach((face) => {
        if (face.family === family) document.fonts.delete(face)
      })

      const fontFace = new FontFace(family, `url("${font.font_url}")`)
      fontPromises.push(fontFace.load())
      document.fonts.add(fontFace)
      document.documentElement.style.setProperty(cssVar, `${family}, sans-serif`)
      document.documentElement.style.setProperty(`${cssVar}-size-multiplier`, font.size_multiplier || 1)
    },
    async setFonts(newVal) {
      const fontPromises = []

      this.registerFont('LBRegularFont', '--regular-font', newVal.fonts.regular_font, fontPromises)
      this.registerFont('LBHeaderFont', '--header-font', newVal.fonts.header_font, fontPromises)
      this.registerFont('LBRegularBoldFont', '--regular-bold-font', newVal.fonts.regular_bold_font, fontPromises)
      // No button font configured: reuse the header font file, but keep the
      // button multiplier at 1 rather than inheriting the header's.
      const buttonFont = newVal.fonts.button_font || { font_url: newVal.fonts.header_font.font_url }
      this.registerFont('LBButtonFont', '--button-font', buttonFont, fontPromises)

      await Promise.all(fontPromises)
    },
    setStyling(newVal) {
      document.documentElement.style.setProperty('--primary-color', newVal.primary_color)
      document.documentElement.style.setProperty('--primary-text-color', newVal.primary_text_color)
      document.documentElement.style.setProperty('--background-color', newVal.background_color)
      document.documentElement.style.setProperty(
        '--border-color',
        this.branding.dark_mode_enabled ? '#3b3b3b' : '#e6e3e3'
      )
      document.documentElement.style.setProperty(
        '--passive-color',
        newVal.passive_color ? newVal.passive_color : '#6b6b6b'
      )
      document.documentElement.style.setProperty('--gray-color', newVal.gray_color ? newVal.gray_color : '#EFEFEF')
      document.documentElement.style.setProperty(
        '--focus-color',
        pickFocusColor([newVal.primary_color, newVal.background_color, newVal.gray_color || '#EFEFEF'])
      )
      document.documentElement.style.setProperty(
        '--text-color',
        this.branding.dark_mode_enabled ? '#ffffff' : newVal.fonts.regular_font.font_color
      )
      document.documentElement.style.setProperty(
        '--button-radius',
        newVal.display_options?.button_radius?.enabled ? newVal.display_options.button_radius.radius + 'px' : '5px'
      )
      document.documentElement.style.setProperty(
        '--header-text-color',
        this.branding.dark_mode_enabled ? '#ffffff' : newVal.fonts.header_font.font_color
      )
      document.documentElement.style.setProperty(
        '--custom-green-color',
        newVal.display_options?.custom_green_color ? newVal.display_options.custom_green_color : '#17873e'
      )
      document.documentElement.style.setProperty(
        '--success-border-color',
        newVal.other_colors?.rewards?.success_border || '#6FD38E'
      )
      document.documentElement.style.setProperty(
        '--success-surface-color',
        newVal.other_colors?.rewards?.success_surface || '#8FF4C5'
      )
      // Admin gates a partner's Success Text Color on contrast, but never gated
      // this fallback: #009440 is 3.95:1 on the near-white reward card it renders
      // on, short of 4.5:1. #007e37 holds 5.20:1 there and 4.52:1 on Gray, so a
      // future placement cannot quietly reintroduce the failure.
      document.documentElement.style.setProperty(
        '--success-text-color',
        newVal.other_colors?.rewards?.success_text || '#007e37'
      )

      let gradientColors = newVal.other_colors?.rewards?.card_gradient_color || []
      const fallbackGradientColors = [
        'rgba(214, 240, 255, 0.3)',
        'rgba(255, 255, 255, 0.4)',
        'rgba(255, 252, 207, 0.4)',
        'rgba(222, 250, 223, 0.4)',
      ]

      if (gradientColors.length > 0) {
        gradientColors.forEach((colorObj, index) => {
          if (!colorObj || typeof colorObj !== 'object') return
          const hex = colorObj.hex
          const opacity = colorObj.opacity
          if (!hex || typeof hex !== 'string' || !hex.match(/^#[0-9A-Fa-f]{6}$/)) return
          if (opacity === undefined || opacity === null || typeof opacity !== 'number') return
          const rgba = this.hexToRgba(hex, opacity / 100)
          if (rgba) {
            document.documentElement.style.setProperty(`--gradient-color-${index + 1}`, rgba)
          }
        })
      }

      for (let i = 1; i <= 4; i++) {
        const existingColor = document.documentElement.style.getPropertyValue(`--gradient-color-${i}`)
        if (!existingColor) {
          document.documentElement.style.setProperty(`--gradient-color-${i}`, fallbackGradientColors[i - 1])
        }
      }
    },
    hexToRgba(hex, opacity) {
      if (!hex || typeof hex !== 'string' || !hex.match(/^#[0-9A-Fa-f]{6}$/)) return null
      if (opacity === undefined || opacity === null || isNaN(opacity)) return null
      const clampedOpacity = Math.max(0, Math.min(1, opacity))
      try {
        const bigint = parseInt(hex.slice(1), 16)
        const r = (bigint >> 16) & 255
        const g = (bigint >> 8) & 255
        const b = bigint & 255
        return `rgba(${r}, ${g}, ${b}, ${clampedOpacity})`
      } catch (error) {
        console.warn('Error parsing hex color:', hex, error)
        return null
      }
    },
    setHeadTags(newVal) {
      document.querySelector('link[rel="icon"]').href = newVal.favicon_url
      document.title = newVal.title
      localStorage.setItem('title', newVal.title)
      document.querySelector('meta[name="description"]').content = newVal.description
      document.querySelector('meta[name="theme-color"]').content = newVal.background_color
      document.querySelector('meta[property="og:image"]').content = newVal.logo_url
    },
    setInnerHeight() {
      document.documentElement.style.setProperty('--innerHeight', `${window.innerHeight}px`)
      window.addEventListener('resize', () => {
        document.documentElement.style.setProperty('--innerHeight', `${window.innerHeight}px`)
      })
      window.addEventListener('orientationchange', () => {
        document.documentElement.style.setProperty('--innerHeight', `${window.innerHeight}px`)
      })
    },
    preventScrollWhenModalOpen() {
      const toggleBodyOverflow = () => {
        const modalContainer = document.querySelector('.modalContainer')
        document.body.style.overflow = modalContainer ? 'hidden' : 'unset'
      }
      toggleBodyOverflow()
      const observer = new MutationObserver(() => {
        toggleBodyOverflow()
      })
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      })
    },
    checkVersion() {
      let version = '1.2.06'
      if (localStorage.getItem('version') !== version && localStorage.getItem('version') !== null) {
        this.SET_LOGGED_IN(false)
        this.SET_ORDER_DATA(null)
        localStorage.clear()
        localStorage.setItem('version', version)
        location.reload()
      } else if (localStorage.getItem('version') === null) {
        localStorage.setItem('version', version)
      }
    },
    checkDiscountCode() {
      if (this.$route.query.discount_code) {
        localStorage.removeItem('orderId')
        localStorage.removeItem('dayAndTimeSelected')
        this.SET_DAY_AND_TIME_SELECTED(false)
        this.SET_ORDER_DATA(null)
        localStorage.setItem('discountCode', this.$route.query.discount_code)
      }
    },
    checkItemId() {
      if (this.$route.query.menu_item_id) {
        localStorage.setItem('deepLinkItemId', this.$route.query.menu_item_id)
      }
    },
    addPixelScript() {
      const pixelId = this.branding?.marketing_ids?.meta_pixel_identifier
      if (pixelId && analyticsAllowedOnHost()) {
        !(function (f, b, e, v, n, t, s) {
          if (f.fbq) return
          n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
          }
          if (!f._fbq) f._fbq = n
          n.push = n
          n.loaded = !0
          n.version = '2.0'
          n.queue = []
          t = b.createElement(e)
          t.async = !0
          t.src = v
          s = b.getElementsByTagName(e)[0]
          s.parentNode.insertBefore(t, s)
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
        fbq('init', pixelId)
        fbq('track', 'PageView')
      }
    },
  },
  async created() {
    await this.$store.dispatch('getBranding')
    this.$store.dispatch('getStoreConfig')
    if (this.$route.name !== 'OrderQuoteView') {
      this.$store.dispatch('getOrderData')
      this.$store.dispatch('getCustomerData')
    }
    if (localStorage.getItem('selectedStoreId')) {
      this.$store.dispatch('getSelectedStore')
    }
    this.checkVersion()
    this.setInnerHeight()
    this.preventScrollWhenModalOpen()
    this.checkDiscountCode()
    this.checkItemId()
  },
  mounted() {
    this.addPixelScript()
    watchFieldErrors()
    // Text fields match :focus-visible however they were focused, so a field the
    // user clicked, or one a dialog focuses on open, would get the keyboard ring.
    // Note when the last interaction was a pointer so App.css can hold it back.
    const root = document.documentElement
    window.addEventListener('pointerdown', () => root.setAttribute('data-pointer', ''), true)
    window.addEventListener(
      'keydown',
      (e) => {
        const typing = /^(INPUT|TEXTAREA)$/.test(e.target.tagName)
        if (e.key === 'Tab' || (!typing && (e.key === 'Enter' || e.key === ' '))) root.removeAttribute('data-pointer')
      },
      true
    )
  },
}
</script>

<style src="./assets/css/App.css"></style>
